import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import "../styles/Poojas.css";

const API = API_ENDPOINTS.POOJAS;

/* ================= URL & BACKGROUND HELPERS ================= */
export const resolveImageUrl = (img) => {
  if (!img || typeof img !== "string") return "";
  const trimmed = img.trim();
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) return trimmed;
  if (trimmed.startsWith("/images/") || trimmed.startsWith("/assets/")) return trimmed;

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    if (typeof window !== "undefined" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
      if (trimmed.includes("localhost:5000") || trimmed.includes("127.0.0.1:5000")) {
        return trimmed.replace(/^http:\/\/(localhost|127\.0\.0\.1):5000/, API_ENDPOINTS.BASE_URL);
      }
    }
    return trimmed;
  }

  return `${API_ENDPOINTS.BASE_URL}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
};

export const getPoojaBackground = (name = "") => {
  const n = (name || "").toLowerCase();
  if (n.includes("kalasam") || n.includes("108"))      return "/images/kalasam_bg.jpg";
  if (n.includes("gomata") || n.includes("cow"))        return "/images/gomata_darshan_bg.jpg";
  if (n.includes("dhuni"))                              return "/images/dhuni_pooja_bg.jpg";
  if (n.includes("santhana") || n.includes("bhagya"))   return "/images/santhana_bhagya_bg.jpg";
  if (n.includes("ghoshala") || n.includes("gho") || n.includes("go pooja")) return "/images/gho-pooja.jpg";
  if (n.includes("amavasya"))                           return "/images/amavasya_rituals_bg.jpg";
  if (n.includes("sathya narayana") || n.includes("satyanarayana")) return "/images/sathya_narayana_bg.jpg";
  if (n.includes("murugan"))                            return "/images/lord_murugan_bg.jpg";
  return null;
};

export const getPoojaCardBackground = (pooja) => {
  if (pooja?.images && Array.isArray(pooja.images) && pooja.images.length > 0) {
    const firstValid = pooja.images.find((img) => typeof img === "string" && img.trim() !== "");
    if (firstValid) {
      return resolveImageUrl(firstValid);
    }
  }
  return getPoojaBackground(pooja?.name || "");
};

/* ================= INDIVIDUAL POOJA CARD COMPONENT ================= */
const PoojaCard = ({ pooja, navigate, onBookPooja }) => {
  const primaryBg = getPoojaCardBackground(pooja);
  const fallbackBg = getPoojaBackground(pooja?.name || "");
  const [bgSrc, setBgSrc] = useState(primaryBg);

  useEffect(() => {
    setBgSrc(getPoojaCardBackground(pooja));
  }, [pooja]);

  const handleImgError = () => {
    if (fallbackBg && bgSrc !== fallbackBg) {
      setBgSrc(fallbackBg);
    }
  };

  return (
    <div
      className={`pooja-card${bgSrc ? " pooja-card--bg" : ""}`}
      style={bgSrc ? {
        backgroundImage: `url("${bgSrc}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      } : {}}
    >
      {/* Preloader to detect image error and fall back gracefully */}
      {bgSrc && fallbackBg && bgSrc !== fallbackBg && (
        <img
          src={bgSrc}
          alt=""
          style={{ display: "none" }}
          onError={handleImgError}
        />
      )}

      {/* Dark overlay only when bg image exists */}
      {bgSrc && <div className="pooja-card-overlay" />}

      <div className="pooja-top" style={{ position: "relative", zIndex: 1 }}>
        <span className="pooja-time">
          {pooja.time || "N/A"}
        </span>
      </div>

      <h2 style={{ position: "relative", zIndex: 1, color: bgSrc ? "#fff" : "#102542" }}>
        {pooja.name}
      </h2>

      <p style={{ position: "relative", zIndex: 1, color: bgSrc ? "rgba(255,255,255,0.88)" : "#555" }}>
        {(pooja.desc || "").length > 100
          ? pooja.desc.substring(0, 100) + "..."
          : pooja.desc}
      </p>

      <button
        className="card-btn"
        style={{ position: "relative", zIndex: 1 }}
        onClick={() => navigate(`/poojas/${pooja._id}`)}
      >
        Read More
      </button>
    </div>
  );
};

export const DEFAULT_HIGHLIGHT_POOJAS = [
  {
    _id: "default-108-kalasam",
    name: "108 Kalasams Blessing",
    icon: "✨",
    time: "Temple Feature",
    desc: "Each Kalasam is engraved with Baba’s 108 sacred names (Ashtothram), symbolizing divine energy and protection.\n\n",
    images: []
  },
  {
    _id: "default-gomata-darshan",
    name: "Gomata Darshan Miracle",
    icon: "🐄",
    time: "Daily Darshan",
    desc: "A sacred cow climbs 23 steps daily to have Baba’s darshan, believed to remove karma and Pitru doshas.\n\n",
    images: []
  },
  {
    _id: "default-dhuni-pooja",
    name: "Dhuni Pooja",
    icon: "🔥",
    time: "Daily",
    desc: "A sacred fire ritual performed daily to purify karma, remove negative energies, and spread spiritual healing.\n\n",
    images: []
  },
  {
    _id: "default-santhana-bhagya",
    name: "Santhana Bhagya Pooja",
    icon: "🌸",
    time: "Special Ritual",
    desc: "Performed for blessings of children, family happiness, and fulfillment of parenthood wishes.\n\n",
    images: []
  },
  {
    _id: "default-daily-gho",
    name: "Daily Gho Pooja",
    icon: "🐄",
    time: "7:00 AM",
    desc: "A daily ritual of worshipping sacred cows, symbolizing purity and nourishment. It is believed to bring peace, prosperity, and divine blessings.\n\n",
    images: []
  },
  {
    _id: "default-amavasya-special",
    name: "Amavasya Special Pooja",
    icon: "🌙",
    time: "9:30 AM",
    desc: "Special new moon pooja for ancestral peace, spiritual cleansing, and removal of doshas.\n\n",
    images: []
  },
  {
    _id: "default-sathya-narayana",
    name: "Sathya Narayana Pooja",
    icon: "🪔",
    time: "Monthly",
    desc: "A divine ceremony for prosperity, peace, and wish fulfillment performed with devotion and sacred chanting.\n\n",
    images: []
  },
  {
    _id: "default-lord-murugan",
    name: "Lord Murugan Divine Presence",
    icon: "🪔",
    time: "Special Ritual",
    desc: "Special poojas for Kalyanasubramanya Swamy during Thaipusam, Skanda Sashti, and Vaikasi Visakam.\n\n",
    images: []
  }
];

const Poojas = () => {
  const [poojas, setPoojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  /* ================= FETCH FROM BACKEND ================= */
  const fetchPoojas = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(API);

      if (!res.ok) {
        throw new Error("Failed to fetch poojas");
      }

      const data = await res.json();
      
      // Ensure all 8 temple highlight rituals are present
      const existingNames = (data || []).map((p) => (p?.name || "").toLowerCase());
      const missingDefaults = DEFAULT_HIGHLIGHT_POOJAS.filter((def) => {
        const defName = def.name.toLowerCase();
        return !existingNames.some(
          (n) => n.includes(defName) || 
                 defName.includes(n) || 
                 (defName.includes("gomata") && n.includes("gomata")) || 
                 (defName.includes("murugan") && n.includes("murugan"))
        );
      });

      const highlightOrder = [
        "108 kalasam",
        "gomata",
        "dhuni",
        "santhana",
        "gho",
        "amavasya",
        "satya",
        "sathya",
        "murugan"
      ];

      const getOrderRank = (p) => {
        const n = (p?.name || "").toLowerCase();
        for (let idx = 0; idx < highlightOrder.length; idx++) {
          if (n.includes(highlightOrder[idx])) return idx;
        }
        return 999;
      };

      const combined = [...(data || []), ...missingDefaults];
      combined.sort((a, b) => getOrderRank(a) - getOrderRank(b));

      setPoojas(combined);

    } catch (err) {
      console.error("Error fetching poojas:", err);
      // Fallback to default highlights if network error
      setPoojas(DEFAULT_HIGHLIGHT_POOJAS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoojas();
  }, []);

  /* ================= LOADING ================= */
  if (loading) {
    return <h2 style={{ textAlign: "center" }}>Loading Poojas...</h2>;
  }

  /* ================= ERROR ================= */
  if (error) {
    return <h2 style={{ textAlign: "center", color: "red" }}>{error}</h2>;
  }

  return (
    <section className="pooja-section">

      {/* LEFT CONTENT */}
      <div className="pooja-left">
        <span className="pooja-tag">DIVINE RITUALS</span>

        <h1>
          Sacred Temple <br />
          Poojas & Blessings
        </h1>

        <p>
          Experience the spiritual power of timeless rituals at Sri Gnana Sai Baba Temple.
          Every pooja is performed with devotion, divine chants, and sacred traditions.
        </p>

        <button
          className="explore-btn"
          onClick={() => navigate("/about")}>
          Explore Temple Rituals
        </button>
      </div>

      {/* RIGHT CARDS */}
      <div className="pooja-right">
        {poojas.map((pooja) => (
          <PoojaCard key={pooja._id} pooja={pooja} navigate={navigate} />
        ))}
      </div>

    </section>
  );
};

export default Poojas;